import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Button, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { gql } from "generated-graphql";
import {
  InternalTagsByNameQuery,
  InternalTagsByNameQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { INTERNAL_TAGS_BY_NAME_QUERY } from "queries/internalTagsByName";
import { useState } from "react";

import { useTagSchema } from "./createTagSchema";
import { TagDialog, TagFormType } from "./TagDialog";

const DELETE_INTERNAL_TAG_QUERY = gql(`
  mutation DeleteInternalTag($tagName: String!) {
    deleteInternalTag(tagName: $tagName)
  }
`);

const UPSERT_INTERNAL_TAG = gql(`
  mutation UpsertInternalTag($tagName: String!, $newTagName: String, $facilityIds: [ID!]) {
    upsertInternalTag(tagName: $tagName, newTagName: $newTagName, facilityIds: $facilityIds)
  }
`);

type InternalTag = {
  name: string;
  facilityCount: number;
};

export const Tags = () => {
  useBreadcrumb({
    label: "Tags",
  });

  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [editingTag, setEditingTag] = useState<InternalTag | null>(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<InternalTag | null>(null);

  const theme = useTheme();
  const alerts = useAlerts();
  const tagSchema = useTagSchema();

  const [upsertInternalTag, { loading: saving }] = useMutation(UPSERT_INTERNAL_TAG, {
    refetchQueries: [INTERNAL_TAGS_BY_NAME_QUERY],
  });

  const [deleteTag, { loading: deleting }] = useMutation(DELETE_INTERNAL_TAG_QUERY, {
    refetchQueries: [INTERNAL_TAGS_BY_NAME_QUERY],
  });

  const handleAddTag = () => {
    setEditingTag(null);
    setShowAddEditDialog(true);
  };

  const handleEditTag = (tag: InternalTag) => {
    setEditingTag(tag);
    setShowAddEditDialog(true);
  };

  const handleDeleteTag = () => {
    if (!tagToDelete) return;
    const promise = deleteTag({
      variables: {
        tagName: tagToDelete.name,
      },
      onCompleted: () => {
        alerts.success("Tag deleted");
      },
      onError: (error) => {
        alerts.error(`Error deleting tag: ${error.message}`);
      },
    });
    setShowDeleteConfirmDialog(false);
    setTagToDelete(null);
    return promise;
  };

  const handleCloseDialog = () => {
    setShowAddEditDialog(false);
    setEditingTag(null);
  };

  const handleSaveTag = async (tagData: TagFormType) => {
    await upsertInternalTag({
      variables: {
        tagName: tagData.tagName,
        newTagName: tagData.newTagName,
        facilityIds: tagData.facilityIds,
      },
      onCompleted: () => {
        alerts.success("Tag saved");
      },
      onError: (error) => {
        alerts.error(`Error upserting tag: ${error.message}`);
      },
      refetchQueries: [INTERNAL_TAGS_BY_NAME_QUERY],
    });
    handleCloseDialog();
  };

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<InternalTagsByNameQuery, InternalTagsByNameQueryVariables, InternalTag>
        dataQuery={INTERNAL_TAGS_BY_NAME_QUERY}
        persistenceKey="internal-tags-by-name"
        initialSortModel={[{ field: "name", sort: "asc" }]}
        excludeFilterColumns={["facilityCount", "count"]}
        getCount={(data) => data.internalTagsByName.count}
        getItems={(data) => data.internalTagsByName.items}
        getRowId={(row) => row.name}
        onRowClick={(params) => handleEditTag(params.row)}
        showManageColumns={false}
        commandButtons={[
          <Button key="add-tag-button" variant="contained" onClick={handleAddTag}>
            Add Tag
          </Button>,
        ]}
        columns={[
          {
            field: "name",
            headerName: "Tag",
            flex: 1,
          },
          {
            field: "facilityCount",
            headerName: "Facilities",
            flex: 0.5,
            type: "number",
            sortable: false,
          },
          {
            field: "actions",
            type: "actions",
            sortable: false,
            align: "right",
            getActions: (params) => {
              const actions = [
                <Tooltip title="Edit Tag" key={1}>
                  <GridActionsCellItem
                    onClick={() => handleEditTag(params.row)}
                    label="Edit Tag"
                    icon={<Edit />}
                    disabled={deleting}
                  />
                </Tooltip>,
                <Tooltip title="Delete Tag" key={2}>
                  <GridActionsCellItem
                    onClick={() => {
                      setTagToDelete(params.row);
                      setShowDeleteConfirmDialog(true);
                    }}
                    label="Delete Tag"
                    icon={<Delete />}
                    disabled={deleting}
                  />
                </Tooltip>,
              ];

              return actions;
            },
          },
        ]}
        noDataMessage="No internal tags found."
      />

      {showAddEditDialog && (
        <TagDialog
          open={showAddEditDialog}
          onClose={handleCloseDialog}
          tag={editingTag}
          onSave={handleSaveTag}
          saving={saving}
          validationResolver={yupResolver(tagSchema) as any}
        />
      )}
      <ConfirmDialog
        open={showDeleteConfirmDialog}
        onClose={() => setShowDeleteConfirmDialog(false)}
        header={`Delete Tag ${tagToDelete?.name}?`}
        confirmText="Yes, I'm sure"
        cancelText="No, cancel"
        onConfirm={handleDeleteTag}
        info={
          <>
            <Typography variant="body2" mb={theme.spacing(2)}>
              Tag{" "}
              <Typography variant="body2" component="span" sx={{ fontStyle: "italic" }}>
                {tagToDelete?.name}
              </Typography>{" "}
              is associated with {tagToDelete?.facilityCount} facilities.
            </Typography>
            <Typography variant="body2">Are you sure you want to remove this tag?</Typography>
          </>
        }
        loading={deleting}
        disableConfirm={!tagToDelete}
      />
    </Stack>
  );
};
